// Curated icon enum — source of truth for every SBI surface that accepts
// an `icon?` field: `sbi.viz.table actions[]`, `sbi.viz.kpi`,
// `sbi.viz.<chart>` title icons, `sbi.ui.section` / `accordion` /
// `callout` / `tabs`, and `:icon-<name>:` markdown shortcodes in
// `sbi.ui.text`. The worker-runtime inlines a copy of the name set
// (see `worker-runtime.ts → CURATED_ICON_SET`) and a vitest check
// asserts the two stay in sync.
//
// **Insights are read-only.** This list excludes every icon whose
// default semantic prior implies a side-effect — no `trash` (delete),
// no `pencil` (edit), no `download` (export), no `mail` / `share`
// (send), no `bookmark-plus` / `heart-plus` (mutating like / favourite
// state). Authors can only navigate.
//
// The list is ~200 icons grouped into 19 categories. The per-icon
// metadata (`description`, `tags`, `category`) powers the
// `lucent_icon_search` MCP tool — authors who can't recall the exact
// name search by concept or category and the server returns ranked
// matches. The briefing topic `lucent_insights_topic('icons')` lists
// the same 200 names with their descriptions inline.
//
// Library is deliberately not named in author-facing surfaces (we may
// run our own glyph set later). Internally the runtime maps these
// kebab-case names to PascalCase Vue components in
// `InsightTable.vue → LUCIDE_ICON_COMPONENT`.

export const LUCIDE_ICONS = [
  // People & identity (11)
  "users",
  "user",
  "contact",
  "smile",
  "frown",
  "glasses",
  "accessibility",
  "baby",
  "person-standing",
  "hand",
  "footprints",
  // Body & wellness (10)
  "brain",
  "ear",
  "eye",
  "heart",
  "heart-pulse",
  "activity",
  "dumbbell",
  "stethoscope",
  "thermometer",
  // Food & drink (12)
  "apple",
  "salad",
  "utensils",
  "coffee",
  "cup-soda",
  "martini",
  "wine",
  "beer",
  "cake",
  "pizza",
  "sandwich",
  "soup",
  // Places & buildings (14)
  "building",
  "building-2",
  "house",
  "store",
  "factory",
  "school",
  "hospital",
  "hotel",
  "warehouse",
  "landmark",
  "castle",
  "church",
  "tent",
  "bed",
  // Nature & geography (11)
  "trees",
  "tree-deciduous",
  "tree-pine",
  "mountain",
  "waves",
  "droplet",
  "flame",
  "flower",
  "leaf",
  "globe",
  "fence",
  // Travel & transport (10)
  "car",
  "truck",
  "bike",
  "bus",
  "train-front",
  "plane",
  "ship",
  "anchor",
  "fuel",
  "square-parking",
  // Navigation & maps (3)
  "navigation",
  "route",
  "map-pin",
  // Time & dates (10)
  "calendar",
  "calendar-days",
  "calendar-clock",
  "calendar-range",
  "clock",
  "alarm-clock",
  "timer",
  "hourglass",
  "history",
  "watch",
  // Weather (6)
  "sun",
  "moon",
  "sunrise",
  "sunset",
  "cloud",
  "cloud-rain",
  "snowflake",
  // Money & finance (14)
  "dollar-sign",
  "euro",
  "pound-sterling",
  "banknote",
  "coins",
  "credit-card",
  "wallet",
  "piggy-bank",
  "receipt",
  "percent",
  "calculator",
  "scale",
  "hand-coins",
  "badge-dollar-sign",
  // Charts & analytics (10)
  "chart-bar",
  "chart-line",
  "chart-pie",
  "chart-area",
  "chart-column",
  "chart-scatter",
  "chart-candlestick",
  "trending-up",
  "trending-down",
  "gauge",
  // Documents & data (12)
  "file-text",
  "folder",
  "folder-open",
  "paperclip",
  "clipboard",
  "clipboard-list",
  "book",
  "book-open",
  "library",
  "archive",
  "layers",
  "database",
  // Communication (5)
  "phone",
  "message-square",
  "message-circle",
  "bell",
  "megaphone",
  // Devices & tech (8)
  "computer",
  "laptop",
  "smartphone",
  "monitor",
  "keyboard",
  "headphones",
  "camera",
  // Arrows & directions (10)
  "arrow-right",
  "arrow-left",
  "arrow-up",
  "arrow-down",
  "chevron-right",
  "chevron-left",
  "chevron-up",
  "chevron-down",
  "corner-up-right",
  "move",
  // Links & overflow (4)
  "external-link",
  "link",
  "ellipsis",
  "ellipsis-vertical",
  // Status signals (12)
  "info",
  "triangle-alert",
  "circle-check",
  "circle-help",
  "circle-x",
  "octagon-alert",
  "octagon-x",
  "shield",
  "shield-check",
  "lock",
  "check",
  "x",
  // Tags & labels (8)
  "tag",
  "tags",
  "hash",
  "bookmark",
  "flag",
  "pin",
  "star",
  "badge",
  // UI controls (8)
  "filter",
  "search",
  "settings",
  "sliders-horizontal",
  "list",
  "table",
  "columns-3",
  "grid-3x3",
  // Shapes & containers (8)
  "circle",
  "square",
  "triangle",
  "hexagon",
  "diamond",
  "box",
  "package",
  "gift",
  // Recognition & rewards (8)
  "trophy",
  "medal",
  "award",
  "target",
  "goal",
  "ticket",
  "crown",
  "gem",
  // Misc (6)
  "sparkles",
  "zap",
  "graduation-cap",
  "notebook",
  "lightbulb",
  "key",
  "wrench",
] as const;

export type LucideIconName = (typeof LUCIDE_ICONS)[number];

const LUCIDE_ICON_SET: ReadonlySet<string> = new Set(LUCIDE_ICONS);

export function isLucideIconName(value: unknown): value is LucideIconName {
  return typeof value === "string" && LUCIDE_ICON_SET.has(value);
}

// Per-icon metadata: powers the `lucent_icon_search` MCP tool and the
// `icons` briefing topic. Every name in `LUCIDE_ICONS` must have an
// entry here — the structural test in `lucide-icons.test.ts` asserts
// the two stay in sync.
//
// `description` is the one-line "use for X — NOT for Y" guidance.
// `tags` are concept-level search synonyms (the LLM may search for
// `chart`, `analytics`, `dashboard`, `trend` — those all rank
// `chart-line` / `trending-up`).
// `category` groups icons in the briefing topic and lets callers
// filter the search result by domain.
export interface IconMeta {
  description: string;
  tags: readonly string[];
  category: string;
}

export const ICON_METADATA: Readonly<Record<LucideIconName, IconMeta>> = {
  // People & identity
  users: { description: "groups, teams, cohorts, member lists", tags: ["group", "team", "cohort", "members", "people", "audience"], category: "people" },
  user: { description: "single member, single contact, single profile", tags: ["person", "profile", "account", "individual"], category: "people" },
  contact: { description: "contact record or address-book entry", tags: ["contact", "address-book", "directory"], category: "people" },
  smile: { description: "positive sentiment, satisfaction, NPS detractor↔promoter", tags: ["positive", "happy", "nps", "satisfaction", "sentiment"], category: "people" },
  frown: { description: "negative sentiment, unhappy, NPS detractor", tags: ["negative", "unhappy", "detractor", "sentiment"], category: "people" },
  glasses: { description: "view profile, inspect details, reading view", tags: ["read", "inspect", "details", "view-profile"], category: "people" },
  accessibility: { description: "accessibility flags, ADA / WCAG indicators", tags: ["a11y", "accessibility", "ada", "wcag"], category: "people" },
  baby: { description: "child / junior member, age-segmented cohort", tags: ["child", "infant", "junior", "kids"], category: "people" },
  "person-standing": { description: "single attendee, standing roster entry", tags: ["attendee", "person", "standing", "individual"], category: "people" },
  hand: { description: "manual entry, raised-hand attention, sign-language", tags: ["manual", "raise", "wave", "attention"], category: "people" },
  footprints: { description: "activity log, attendance trail, visit history", tags: ["steps", "activity", "visits", "trail", "history"], category: "people" },

  // Body & wellness
  brain: { description: "AI / analytics insight, decision logic", tags: ["ai", "thinking", "logic", "intelligence"], category: "wellness" },
  ear: { description: "audio settings, hearing-related, listening surface", tags: ["audio", "listen", "sound"], category: "wellness" },
  eye: { description: "view, preview, visibility toggle (read-only)", tags: ["view", "preview", "show", "visible"], category: "wellness" },
  heart: { description: "favourites view, beloved list (read-only — does NOT add a favourite)", tags: ["favourite", "loved", "wishlist", "saved"], category: "wellness" },
  "heart-pulse": { description: "vital signs, system health, member fitness", tags: ["pulse", "health", "vitals", "ecg", "monitor"], category: "wellness" },
  activity: { description: "activity feed, engagement timeline, fitness signal", tags: ["pulse", "engagement", "active", "feed"], category: "wellness" },
  dumbbell: { description: "fitness, gym session, strength training", tags: ["gym", "fitness", "strength", "workout", "training"], category: "wellness" },
  stethoscope: { description: "medical record view, health-check drilldown", tags: ["medical", "doctor", "diagnosis", "health"], category: "wellness" },
  thermometer: { description: "performance temperature, fever index, hot/cold metric", tags: ["temperature", "hot", "cold", "heat"], category: "wellness" },

  // Food & drink
  apple: { description: "nutrition data, healthy-food category, fruit aisle", tags: ["fruit", "food", "nutrition", "healthy"], category: "food" },
  salad: { description: "menu items, healthy options, café orders", tags: ["food", "menu", "healthy", "veggie"], category: "food" },
  utensils: { description: "dining, restaurant orders, meal bookings", tags: ["dining", "food", "restaurant", "meal"], category: "food" },
  coffee: { description: "café, café visits, morning routine", tags: ["cafe", "drink", "coffee", "morning"], category: "food" },
  "cup-soda": { description: "soft-drink sales, beverage line items", tags: ["drink", "soda", "beverage", "soft-drink"], category: "food" },
  martini: { description: "cocktail menu, bar sales, premium beverages", tags: ["bar", "cocktail", "drink", "alcohol"], category: "food" },
  wine: { description: "wine list, bar sales, cellar inventory", tags: ["bar", "wine", "drink", "alcohol"], category: "food" },
  beer: { description: "beer sales, tap inventory, brewery orders", tags: ["bar", "beer", "drink", "alcohol"], category: "food" },
  cake: { description: "birthdays, celebrations, café desserts", tags: ["birthday", "dessert", "celebration"], category: "food" },
  pizza: { description: "food orders, kitchen menu, catering events", tags: ["food", "meal", "delivery"], category: "food" },
  sandwich: { description: "café menu, light meal item", tags: ["food", "meal", "lunch", "snack"], category: "food" },
  soup: { description: "café menu, comfort food, warm dishes", tags: ["food", "meal", "warm"], category: "food" },

  // Places & buildings
  building: { description: "venue, branch, head office (generic)", tags: ["venue", "branch", "site", "office", "location"], category: "places" },
  "building-2": { description: "alternate venue glyph, complex / campus / multi-site", tags: ["venue", "campus", "site", "complex", "offices"], category: "places" },
  house: { description: "member's home address, residential location", tags: ["home", "address", "residence", "house"], category: "places" },
  store: { description: "pro-shop, retail outlet, store front", tags: ["shop", "retail", "merchant", "storefront"], category: "places" },
  factory: { description: "production site, manufacturing, kit / equipment supply", tags: ["industry", "production", "supply"], category: "places" },
  school: { description: "school programme, academy, education provider", tags: ["education", "academy", "school", "kids"], category: "places" },
  hospital: { description: "medical centre, injury record, clinic visit", tags: ["medical", "clinic", "emergency"], category: "places" },
  hotel: { description: "accommodation, away-trip lodging, hospitality", tags: ["accommodation", "lodging", "hospitality"], category: "places" },
  warehouse: { description: "stock holding, fulfilment, equipment storage", tags: ["storage", "stock", "inventory", "fulfilment"], category: "places" },
  landmark: { description: "named landmark, historic site, point-of-interest", tags: ["monument", "landmark", "site", "history"], category: "places" },
  castle: { description: "premium venue, heritage property", tags: ["heritage", "premium", "site"], category: "places" },
  church: { description: "place of worship, ceremony venue", tags: ["religious", "ceremony", "worship"], category: "places" },
  tent: { description: "outdoor event, festival booking, camp", tags: ["camp", "festival", "outdoor", "event"], category: "places" },
  bed: { description: "stay record, overnight booking, accommodation slot", tags: ["sleep", "rest", "accommodation", "bedroom"], category: "places" },

  // Nature & geography
  trees: { description: "outdoor venue, parkland, environmental data", tags: ["forest", "nature", "outdoor", "park"], category: "nature" },
  "tree-deciduous": { description: "single deciduous tree, season indicator", tags: ["tree", "nature", "seasonal"], category: "nature" },
  "tree-pine": { description: "single coniferous tree, winter / forest theme", tags: ["tree", "nature", "evergreen", "winter"], category: "nature" },
  mountain: { description: "outdoor / hike route, scenic venue", tags: ["nature", "outdoor", "hike", "peak"], category: "nature" },
  waves: { description: "pool / water-sport venue, beachfront", tags: ["water", "pool", "beach", "wave"], category: "nature" },
  droplet: { description: "hydration metric, water usage, blood/donation", tags: ["water", "fluid", "drop", "wet"], category: "nature" },
  flame: { description: "trending content, hot streak, popularity", tags: ["fire", "hot", "trending", "popular"], category: "nature" },
  flower: { description: "spring season, garden venue, decorative theme", tags: ["bloom", "garden", "decorative", "nature"], category: "nature" },
  leaf: { description: "sustainability, eco-friendly, plant-based menu", tags: ["eco", "nature", "green", "plant", "sustainable"], category: "nature" },
  globe: { description: "international scope, world map, region selector", tags: ["world", "earth", "global", "international"], category: "nature" },
  fence: { description: "venue boundary, perimeter, restricted area", tags: ["boundary", "perimeter", "fence", "border"], category: "nature" },

  // Travel & transport
  car: { description: "drive-to venue, member arrival mode, vehicle", tags: ["drive", "vehicle", "automobile", "transport"], category: "transport" },
  truck: { description: "delivery, equipment haul, logistics", tags: ["delivery", "haul", "logistics", "freight"], category: "transport" },
  bike: { description: "cyclist, bike commute, fitness ride", tags: ["bicycle", "cycle", "ride", "cyclist"], category: "transport" },
  bus: { description: "public transit, group transport, shuttle", tags: ["transit", "public", "shuttle", "coach"], category: "transport" },
  "train-front": { description: "rail transit, commuter rail", tags: ["rail", "train", "transit", "commute"], category: "transport" },
  plane: { description: "flight, away trip, international travel", tags: ["flight", "airline", "fly", "trip"], category: "transport" },
  ship: { description: "boat, cruise, water-based travel", tags: ["boat", "cruise", "vessel", "sea"], category: "transport" },
  anchor: { description: "pinned / anchored item, harbour, dock", tags: ["pin", "anchor", "harbour", "dock"], category: "transport" },
  fuel: { description: "fuel record, refuelling, energy", tags: ["gas", "petrol", "refuel", "energy"], category: "transport" },
  "square-parking": { description: "parking lot, bay assignment, P-bay marker", tags: ["parking", "bay", "lot", "p"], category: "transport" },
  navigation: { description: "GPS / direction pointer, route start", tags: ["gps", "direction", "compass", "route-start"], category: "transport" },
  route: { description: "planned route, travel path, journey leg", tags: ["path", "journey", "directions", "way"], category: "transport" },
  "map-pin": { description: "specific place on a map — `field of play`, `court`, `address pin`. NOT for a venue (use `building` — pins read as transient locations)", tags: ["pin", "location", "marker", "place", "address"], category: "transport" },

  // Time & dates
  calendar: { description: "bookings, events, date — most-reached time icon", tags: ["date", "schedule", "event", "booking", "agenda"], category: "time" },
  "calendar-days": { description: "month grid, week-by-week view", tags: ["month", "week", "calendar", "grid"], category: "time" },
  "calendar-clock": { description: "scheduled-time slot, time-bound booking", tags: ["schedule", "time-slot", "calendar"], category: "time" },
  "calendar-range": { description: "date range, multi-day span, season", tags: ["range", "span", "between-dates"], category: "time" },
  clock: { description: "time / duration / session length", tags: ["time", "duration", "minutes", "hours"], category: "time" },
  "alarm-clock": { description: "reminder, alert at time, wake-up", tags: ["alarm", "reminder", "alert", "wake"], category: "time" },
  timer: { description: "elapsed timer, stopwatch, session running", tags: ["stopwatch", "elapsed", "countdown"], category: "time" },
  hourglass: { description: "pending / waiting / in-progress", tags: ["wait", "pending", "elapsed", "progress"], category: "time" },
  history: { description: "history view, past records, audit trail", tags: ["past", "audit", "log", "revert", "timeline"], category: "time" },
  watch: { description: "wearable device, time-on-wrist, fitness watch", tags: ["wearable", "wrist", "device", "smartwatch"], category: "time" },

  // Weather
  sun: { description: "day, sunny weather, daytime indicator", tags: ["day", "sunny", "weather", "warm"], category: "weather" },
  moon: { description: "night, dark mode, evening shift", tags: ["night", "dark", "evening", "lunar"], category: "weather" },
  sunrise: { description: "morning, dawn, early time-of-day", tags: ["morning", "dawn", "early", "am"], category: "weather" },
  sunset: { description: "evening, dusk, late time-of-day", tags: ["evening", "dusk", "pm", "twilight"], category: "weather" },
  cloud: { description: "cloud storage / sync, weather cloudy", tags: ["weather", "storage", "sync", "overcast"], category: "weather" },
  "cloud-rain": { description: "rainy weather, wet conditions, rainfall data", tags: ["weather", "rain", "wet", "precipitation"], category: "weather" },
  snowflake: { description: "snow, winter season, cold storage", tags: ["snow", "winter", "cold", "ice"], category: "weather" },

  // Money & finance
  "dollar-sign": { description: "currency-valued drilldown, revenue / payment (USD)", tags: ["currency", "money", "usd", "price"], category: "money" },
  euro: { description: "euro-currency record, EU finance", tags: ["currency", "money", "eur"], category: "money" },
  "pound-sterling": { description: "GBP-currency record, UK finance", tags: ["currency", "money", "gbp", "uk"], category: "money" },
  banknote: { description: "cash payments, paper money", tags: ["cash", "money", "note", "bill"], category: "money" },
  coins: { description: "coin payments, change, micro-transactions", tags: ["cash", "money", "change", "coin"], category: "money" },
  "credit-card": { description: "card payment record, billing method", tags: ["card", "payment", "billing", "credit"], category: "money" },
  wallet: { description: "wallet / balance / stored value", tags: ["balance", "credit", "stored-value"], category: "money" },
  "piggy-bank": { description: "savings, member fund, prepaid balance", tags: ["savings", "fund", "balance", "prepaid"], category: "money" },
  receipt: { description: "transaction receipt, invoice line", tags: ["invoice", "transaction", "billing", "proof"], category: "money" },
  percent: { description: "rate / discount / share / proportion", tags: ["rate", "share", "discount", "proportion"], category: "money" },
  calculator: { description: "financial calc, pricing breakdown", tags: ["calc", "math", "pricing", "compute"], category: "money" },
  scale: { description: "balance, comparison, weighing two sides", tags: ["balance", "compare", "weigh", "fair"], category: "money" },
  "hand-coins": { description: "received funds, tips, hand-over of money", tags: ["payment", "tip", "received", "handover"], category: "money" },
  "badge-dollar-sign": { description: "paid status, premium member, value badge", tags: ["paid", "premium", "value", "badge"], category: "money" },

  // Charts & analytics
  "chart-bar": { description: "categorical breakdown drilldown (bar chart)", tags: ["bar", "categorical", "breakdown", "histogram"], category: "charts" },
  "chart-line": { description: "time-series drilldown (line chart)", tags: ["line", "trend", "time-series", "history"], category: "charts" },
  "chart-pie": { description: "share-of-total breakdown (pie / donut)", tags: ["pie", "share", "proportion", "donut"], category: "charts" },
  "chart-area": { description: "stacked or cumulative trend drilldown", tags: ["area", "cumulative", "stacked"], category: "charts" },
  "chart-column": { description: "vertical bar chart, ranked comparison", tags: ["column", "vertical", "bar", "rank"], category: "charts" },
  "chart-scatter": { description: "scatter / correlation / cluster view", tags: ["scatter", "correlation", "cluster", "points"], category: "charts" },
  "chart-candlestick": { description: "open-high-low-close, financial detail", tags: ["candle", "ohlc", "finance", "stock"], category: "charts" },
  "trending-up": { description: "rising metric, growth signal", tags: ["up", "growth", "rising", "improve"], category: "charts" },
  "trending-down": { description: "falling metric, decline signal", tags: ["down", "decline", "falling", "drop"], category: "charts" },
  gauge: { description: "single-metric gauge, KPI dial", tags: ["dial", "kpi", "single-metric", "speedometer"], category: "charts" },

  // Documents & data
  "file-text": { description: "documents and notes — NOT generic entity references (use `external-link`)", tags: ["document", "note", "page", "file"], category: "documents" },
  folder: { description: "collection, project, grouped entities", tags: ["group", "collection", "directory", "category"], category: "documents" },
  "folder-open": { description: "expanded collection, opened group", tags: ["open", "expanded", "collection"], category: "documents" },
  paperclip: { description: "attached file, association", tags: ["attach", "file", "associated"], category: "documents" },
  clipboard: { description: "task list, checklist record", tags: ["task", "list", "checklist", "todo"], category: "documents" },
  "clipboard-list": { description: "task list with rows, structured checklist", tags: ["list", "checklist", "task", "agenda"], category: "documents" },
  book: { description: "manual, guide, reference doc", tags: ["doc", "manual", "guide", "reference", "literature"], category: "documents" },
  "book-open": { description: "open chapter, reading view", tags: ["open", "chapter", "reading", "library"], category: "documents" },
  library: { description: "knowledge base, archive, catalogue", tags: ["catalogue", "kb", "archive", "shelf"], category: "documents" },
  archive: { description: "archived items (read-only — NOT to archive new items)", tags: ["archived", "stored", "old", "historical"], category: "documents" },
  layers: { description: "stacked layers, composite view, z-order", tags: ["stack", "composite", "z-order", "depth"], category: "documents" },
  database: { description: "data store view, schema, table-set", tags: ["data", "store", "db", "schema"], category: "documents" },

  // Communication (no mutation priors)
  phone: { description: "call log, phone-related drilldown (view, NOT initiate)", tags: ["call", "phone", "voice", "log"], category: "communication" },
  "message-square": { description: "messages thread, chat record (view, NOT send)", tags: ["chat", "thread", "comment", "talk"], category: "communication" },
  "message-circle": { description: "comment thread, conversation (view)", tags: ["chat", "comment", "talk", "conversation"], category: "communication" },
  bell: { description: "notifications, alerts", tags: ["notification", "alert", "reminder"], category: "communication" },
  megaphone: { description: "announcements, broadcast log", tags: ["announce", "broadcast", "loud"], category: "communication" },

  // Devices & tech
  computer: { description: "desktop session, computer device", tags: ["desktop", "pc", "device", "computer"], category: "devices" },
  laptop: { description: "laptop session, portable device", tags: ["portable", "laptop", "device"], category: "devices" },
  smartphone: { description: "mobile session, phone device", tags: ["mobile", "phone", "device", "cell"], category: "devices" },
  monitor: { description: "display, screen, kiosk", tags: ["screen", "display", "kiosk", "tv"], category: "devices" },
  keyboard: { description: "input device, typing record", tags: ["typing", "input", "keys"], category: "devices" },
  headphones: { description: "audio gear, listening session", tags: ["audio", "music", "headset"], category: "devices" },
  camera: { description: "photo capture, image record (view photos)", tags: ["photo", "image", "snap", "capture"], category: "devices" },

  // Arrows & directions
  "arrow-right": { description: "generic-purpose 'go look at this'; default navigation", tags: ["next", "forward", "go", "view"], category: "arrows" },
  "arrow-left": { description: "back / previous, return to prior view", tags: ["back", "previous", "return"], category: "arrows" },
  "arrow-up": { description: "raise, promote, move-up", tags: ["up", "raise", "promote"], category: "arrows" },
  "arrow-down": { description: "lower, descend, move-down", tags: ["down", "lower", "descend"], category: "arrows" },
  "chevron-right": { description: "quieter forward navigation than `arrow-right`", tags: ["next", "expand", "forward"], category: "arrows" },
  "chevron-left": { description: "quieter back navigation than `arrow-left`", tags: ["back", "collapse", "previous"], category: "arrows" },
  "chevron-up": { description: "collapse / scroll up affordance", tags: ["up", "collapse", "less"], category: "arrows" },
  "chevron-down": { description: "expand / scroll down affordance", tags: ["down", "expand", "more"], category: "arrows" },
  "corner-up-right": { description: "branching navigation, forward via a corner", tags: ["branch", "corner", "redirect"], category: "arrows" },
  move: { description: "movable / reposition (read-only — view position)", tags: ["drag", "reposition", "shift"], category: "arrows" },

  // Links & overflow
  "external-link": { description: "navigate to a related entity — generic-purpose 'go look at this'", tags: ["external", "open", "navigate", "go"], category: "links" },
  link: { description: "view linked records, association detail", tags: ["association", "linked", "ref"], category: "links" },
  ellipsis: { description: "RESERVED — used by the mobile-overflow row button. Do not use as a per-action icon.", tags: ["overflow", "more", "menu"], category: "links" },
  "ellipsis-vertical": { description: "RESERVED — reserved for future per-row 'more actions' affordances.", tags: ["overflow", "vertical", "more"], category: "links" },

  // Status signals
  info: { description: "generic 'more info' drilldown — be honest, use noun-icons where they fit better", tags: ["info", "details", "about"], category: "status" },
  "triangle-alert": { description: "view warnings, risk indicators", tags: ["warn", "alert", "risk", "caution"], category: "status" },
  "circle-check": { description: "view passed checks, completed states (read-only — does NOT mark done)", tags: ["check", "pass", "complete", "ok"], category: "status" },
  "circle-help": { description: "help / FAQ / explainer drilldown", tags: ["help", "faq", "question", "explain"], category: "status" },
  "circle-x": { description: "view failed checks, rejected states (read-only — does NOT delete)", tags: ["fail", "rejected", "error", "no"], category: "status" },
  "octagon-alert": { description: "view critical risk, severe warning record", tags: ["critical", "severe", "stop", "danger"], category: "status" },
  "octagon-x": { description: "view blocked / barred items (read-only — does NOT delete)", tags: ["blocked", "barred", "denied", "stop"], category: "status" },
  shield: { description: "permissions / security drilldown", tags: ["security", "permissions", "protect"], category: "status" },
  "shield-check": { description: "verified status, security check passed", tags: ["verified", "secure", "passed", "trusted"], category: "status" },
  lock: { description: "locked / private record (view, NOT to lock)", tags: ["private", "secured", "restricted", "encrypted"], category: "status" },
  check: { description: "generic checkmark, completed item view", tags: ["done", "complete", "tick", "yes"], category: "status" },
  x: { description: "generic close / dismiss-view (read-only, NOT delete)", tags: ["close", "dismiss", "cancel", "no"], category: "status" },

  // Tags & labels
  tag: { description: "single label / category attached to a row", tags: ["label", "category", "metadata"], category: "labels" },
  tags: { description: "multiple labels / tag set", tags: ["labels", "categories", "metadata"], category: "labels" },
  hash: { description: "hashtag / channel / numeric label", tags: ["hashtag", "channel", "topic", "number"], category: "labels" },
  bookmark: { description: "bookmarked / pinned (read-only — view bookmarks, NOT to add)", tags: ["pin", "saved", "marked"], category: "labels" },
  flag: { description: "flagged-for-attention item, priority signal", tags: ["priority", "alert", "marker", "country"], category: "labels" },
  pin: { description: "pinned item / pinned location", tags: ["pin", "stick", "fix"], category: "labels" },
  star: { description: "starred / featured (read-only — view favourites)", tags: ["featured", "favourite", "important"], category: "labels" },
  badge: { description: "badge / certification / status marker", tags: ["badge", "marker", "certified", "status"], category: "labels" },

  // UI controls (read-only-safe)
  filter: { description: "view applied filters / segment criteria", tags: ["narrow", "subset", "criteria"], category: "ui" },
  search: { description: "view search results, scope to a subset", tags: ["find", "lookup", "query"], category: "ui" },
  settings: { description: "view configuration / applied settings (NOT to edit)", tags: ["config", "preferences", "options"], category: "ui" },
  "sliders-horizontal": { description: "view applied parameter settings", tags: ["params", "tune", "adjust", "controls"], category: "ui" },
  list: { description: "list view of records", tags: ["records", "rows", "items"], category: "ui" },
  table: { description: "tabular view of records", tags: ["grid", "rows", "columns", "spreadsheet"], category: "ui" },
  "columns-3": { description: "column layout, kanban-style view", tags: ["kanban", "columns", "layout"], category: "ui" },
  "grid-3x3": { description: "grid layout, tile view", tags: ["grid", "tiles", "layout"], category: "ui" },

  // Shapes & containers
  circle: { description: "neutral shape marker, generic dot", tags: ["dot", "circle", "shape"], category: "shapes" },
  square: { description: "neutral shape marker, generic block", tags: ["block", "square", "shape"], category: "shapes" },
  triangle: { description: "warning shape, alert glyph (alternate to `triangle-alert`)", tags: ["warn", "shape", "alert"], category: "shapes" },
  hexagon: { description: "hex marker, tile shape", tags: ["hex", "tile", "shape"], category: "shapes" },
  diamond: { description: "diamond marker, special highlight", tags: ["diamond", "rhombus", "highlight"], category: "shapes" },
  box: { description: "boxed item, container, shipping unit", tags: ["container", "package", "carton"], category: "shapes" },
  package: { description: "package / parcel / shipment record", tags: ["parcel", "shipment", "delivery"], category: "shapes" },
  gift: { description: "gift / present / referral reward", tags: ["present", "reward", "referral"], category: "shapes" },

  // Recognition & rewards
  trophy: { description: "trophies, championships, top performer", tags: ["championship", "winner", "achievement"], category: "rewards" },
  medal: { description: "medal / placing / commendation", tags: ["placing", "honour", "commendation"], category: "rewards" },
  award: { description: "recognition / achievement record", tags: ["recognition", "achievement", "honour"], category: "rewards" },
  target: { description: "goals, KPIs, performance targets — NOT a map pin", tags: ["goal", "kpi", "objective", "aim"], category: "rewards" },
  goal: { description: "soccer-style goal, sports scoring", tags: ["score", "soccer", "football", "net"], category: "rewards" },
  ticket: { description: "ticket / pass / single-event entry", tags: ["pass", "admission", "entry", "stub"], category: "rewards" },
  crown: { description: "premium tier, top-status, owner", tags: ["royal", "premium", "vip", "owner"], category: "rewards" },
  gem: { description: "valuable / premium tier indicator", tags: ["premium", "valuable", "jewel", "rare"], category: "rewards" },

  // Misc
  sparkles: { description: "highlights, magic / AI / curated picks", tags: ["highlight", "ai", "magic", "curated"], category: "misc" },
  zap: { description: "instant action, fast / lightning, urgent", tags: ["fast", "urgent", "lightning", "power"], category: "misc" },
  "graduation-cap": { description: "course completion, training milestone", tags: ["education", "training", "graduate", "course"], category: "misc" },
  notebook: { description: "notes / journal record (view)", tags: ["notes", "journal", "log"], category: "misc" },
  lightbulb: { description: "ideas, suggestions, insight view", tags: ["idea", "suggestion", "insight", "tip"], category: "misc" },
  key: { description: "access key / credentials / unlock record (view)", tags: ["access", "credential", "unlock", "auth"], category: "misc" },
  wrench: { description: "maintenance log, repair record (view, NOT to repair)", tags: ["maintenance", "repair", "tool", "fix"], category: "misc" },
};

// Levenshtein distance for "did you mean ...?" suggestions. Bounded by
// LUCIDE_ICONS.length (~200) × max name length (~24) so this never
// becomes a hotspot.
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const prev: number[] = new Array<number>(n + 1).fill(0);
  const curr: number[] = new Array<number>(n + 1).fill(0);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      const del = (prev[j] ?? 0) + 1;
      const ins = (curr[j - 1] ?? 0) + 1;
      const sub = (prev[j - 1] ?? 0) + cost;
      curr[j] = Math.min(del, ins, sub);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j] ?? 0;
  }
  return prev[n] ?? 0;
}

/**
 * Return the top-N closest known icon names to a misspelt input.
 * Used by the worker-side validator to surface "did you mean ...?"
 * when an unknown icon name slips through into `actions[i].icon`.
 */
export function suggestIconNames(input: string, n = 3): readonly LucideIconName[] {
  const lower = String(input).toLowerCase();
  return [...LUCIDE_ICONS]
    .map((name) => ({ name, d: levenshtein(lower, name) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, n)
    .map((x) => x.name);
}

// Public search-result shape returned by `searchIcons` and surfaced
// verbatim by the `lucent_icon_search` MCP tool.
export interface IconSearchResult {
  name: LucideIconName;
  description: string;
  category: string;
  tags: readonly string[];
}

/**
 * Search the curated icon set by free-text query. Used by the
 * `lucent_icon_search` MCP tool and the icon-search HTTP endpoint.
 *
 * Ranking, highest score wins:
 *
 *   - exact name match           +1000
 *   - name starts with query     +200
 *   - name contains query         +50
 *   - tag exact match            +400
 *   - tag starts with query       +80
 *   - tag contains query          +20
 *   - description contains query  +10
 *   - category equals query      +150
 *
 * Ties broken by name alphabetical. Returns up to `limit` results
 * (default 10, hard cap 25). Empty / whitespace query returns an
 * empty list — there's no useful "all icons" call shape (use the
 * topic for that).
 */
export function searchIcons(
  query: string,
  limit = 10,
): readonly IconSearchResult[] {
  const q = String(query ?? "").trim().toLowerCase();
  if (q.length === 0) return [];
  const cap = Math.max(1, Math.min(25, limit | 0));

  const ranked: Array<{ name: LucideIconName; score: number }> = [];
  for (const name of LUCIDE_ICONS) {
    const meta = ICON_METADATA[name];
    let score = 0;
    if (name === q) score += 1000;
    else if (name.startsWith(q)) score += 200;
    else if (name.includes(q)) score += 50;
    if (meta.category === q) score += 150;
    for (const tag of meta.tags) {
      if (tag === q) score += 400;
      else if (tag.startsWith(q)) score += 80;
      else if (tag.includes(q)) score += 20;
    }
    if (meta.description.toLowerCase().includes(q)) score += 10;
    if (score > 0) ranked.push({ name, score });
  }
  ranked.sort((a, b) => (b.score - a.score) || a.name.localeCompare(b.name));
  return ranked.slice(0, cap).map(({ name }) => ({
    name,
    description: ICON_METADATA[name].description,
    category: ICON_METADATA[name].category,
    tags: ICON_METADATA[name].tags,
  }));
}
